import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { LoggerUtils } from '@ocean/utils'
import { AppThunk } from 'store'
import { createApiAction } from 'api'
import { clearSessionInfo, userLoggedOut } from 'core/session/slices/session'
import {
  getLegacyMenuConfiguration,
  getStaRedirectLink,
  invokeAproSilentPusher,
  invokeCesSilentPusher,
} from 'core/staIntegration/api/staIntegrationService'
import { StaRedirectParams } from 'core/staIntegration/types'
import { LegacyTestResultsUtils } from 'core/staIntegration/utils'
import {
  StaIntegrationState,
  ApiLegacyMenuConfigurationResponse,
  ApiRedirectLinkResponse,
} from './types'

const initialState: StaIntegrationState = {
  legacyMenuConfiguration: {},
  redirectLink: {},
}

const staIntegrationSlice = createSlice({
  name: 'staIntegration',
  initialState,
  reducers: {
    legacyMenuConfigurationLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiLegacyMenuConfigurationResponse>>
    ) {
      state.legacyMenuConfiguration = action.payload
    },
    redirectLinkLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiRedirectLinkResponse>>
    ) {
      state.redirectLink = action.payload
    },
    silentPushed() {
      LoggerUtils.log('silentPushed')
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const { legacyMenuConfigurationLoaded, redirectLinkLoaded, silentPushed } =
  staIntegrationSlice.actions

export { silentPushed }
export default staIntegrationSlice.reducer

export const loadLegacyMenuConfiguration = () => {
  return createApiAction({
    apiCalls: {
      configuration: getLegacyMenuConfiguration,
    },
    payloadAction: legacyMenuConfigurationLoaded,
  })
}

const handleRedirectToSta =
  (response: ApiMiddlewarePayload): AppThunk =>
  async dispatch => {
    const { error, result } = response
    if (error && error.code === 'GetLegacySessionInvalidSessionResponse') {
      dispatch(clearSessionInfo())
      return
    }

    if (!error && result?.redirectLink) {
      window.location.assign(result.redirectLink)
    }
    dispatch(redirectLinkLoaded(response))
  }

export const redirectToSta = (redirectParams?: StaRedirectParams) => {
  return createApiAction({
    apiCalls: {
      redirectLink: () => getStaRedirectLink(redirectParams),
    },
    payloadAction: redirectLinkLoaded,
    nextAction: handleRedirectToSta,
    shouldShowGlobalLoading: true,
  })
}

export const redirectToStaWithBackUrl =
  (backUrl: string): AppThunk =>
  async () => {
    const result = await getStaRedirectLink()
    if (result.success && result.content) {
      const fullRedirectLink = `${result.content}&backUrl=${encodeURIComponent(
        backUrl.replace('?', '&')
      )}`
      window.location.assign(fullRedirectLink)
    }
  }

export const runLegacyResultsPushers = (): AppThunk => dispatch => {
  dispatch(runLegacyResultsCESPusher())
  dispatch(runLegacyResultsAPROPusher())
}

const runLegacyResultsCESPusher = (): AppThunk => async () => {
  const data = LegacyTestResultsUtils.getCesTestResults()
  if (!data) {
    return
  }
  try {
    const result = await invokeCesSilentPusher(data)
    if (!result.success) {
      LoggerUtils.warn('Ces test result saver failed.')
      return
    }
    const response = result.content?.data

    if (!response || response === '0' || response === 'ERROR') {
      LegacyTestResultsUtils.removeCesTestResults()
    } else {
      LegacyTestResultsUtils.setCesTestResults(response)
    }
  } catch (error) {
    LoggerUtils.warn('Ces test result saver exception.', error)
  }
}

const runLegacyResultsAPROPusher = (): AppThunk => async () => {
  const data = LegacyTestResultsUtils.getAproTestResults()
  if (!data) return
  try {
    const result = await invokeAproSilentPusher(data)
    if (!result.success) {
      LoggerUtils.warn('Apro test result saver failed.')
      return
    }
    LegacyTestResultsUtils.removeAproTestResults()
  } catch (error) {
    LoggerUtils.warn('Apro test result saver exception.', error)
  }
}
